import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState } from "react";

import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  InputGroup,
  Badge,
  ProgressBar,
  Table,
} from "react-bootstrap";
import {
  FaSearch,
  FaIdCard,
  FaTimes,
  FaUsers,
  FaSave,
  FaBriefcase,
  FaMoneyBillWave,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClipboardList,
  FaHistory,
  FaDownload,
  FaUpload,
  FaFileExcel,
  FaFilePdf,
  FaEye,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

// Gradient brand colors (matches the rest of the onboarding module)
const GRADIENT_PRIMARY = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const ACCENT_COLOR = "#f39c12";

function FichaPersonal({ usuario }) {
  // ─── Búsqueda ──────────────────────────────────────────────────────────────
  const [dniInput, setDniInput] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [datosUsuario, setDatosUsuario] = useState(null);
  const [dniActual, setDniActual] = useState("");
  const [error, setError] = useState(null);

  // ─── Ficha cargada desde Firestore ─────────────────────────────────────────
  const [fichaExistente, setFichaExistente] = useState(null);

  // ─── Formulario RRHH ───────────────────────────────────────────────────────
  const [rrhhCargo, setRrhhCargo] = useState("");
  const [rrhhMoneda, setRrhhMoneda] = useState("S/");
  const [rrhhRemuneracion, setRrhhRemuneracion] = useState("");
  const [rrhhFechaInicioLaboral, setRrhhFechaInicioLaboral] = useState("");
  const [rrhhCompleto, setRrhhCompleto] = useState(false);

  // ─── Estado de guardado ────────────────────────────────────────────────────
  const [guardando, setGuardando] = useState(false);
  const [archivoMasivo, setArchivoMasivo] = useState(null);
  const [procesandoMasivo, setProcesandoMasivo] = useState(false);
  const [resultadoMasivo, setResultadoMasivo] = useState({ procesados: 0, errores: 0 });
  const [detalleMasivo, setDetalleMasivo] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const resetForm = () => {
    setRrhhCargo("");
    setRrhhMoneda("S/");
    setRrhhRemuneracion("");
    setRrhhFechaInicioLaboral("");
    setRrhhCompleto(false);
    setFichaExistente(null);
    setDatosUsuario(null);
    setDniActual("");
    setError(null);
  };

  const limpiarBusqueda = () => {
    setDniInput("");
    resetForm();
  };

  const populateFromFicha = (ficha) => {
    setRrhhCargo(ficha.rrhhCargo || "");
    setRrhhMoneda(ficha.rrhhMoneda || "S/");
    setRrhhRemuneracion(ficha.rrhhRemuneracion || "");
    setRrhhFechaInicioLaboral(ficha.rrhhFechaInicioLaboral || "");
    setRrhhCompleto(ficha.rrhhCompleto || false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Búsqueda por DNI
  // ─────────────────────────────────────────────────────────────────────────────
  const buscarPorDNI = async () => {
    const dni = dniInput.trim();
    if (!dni || dni.length < 8) {
      setError("Ingrese un número de DNI válido (mínimo 8 dígitos).");
      return;
    }

    try {
      setBuscando(true);
      setError(null);
      resetForm();

      // 1. Verificar que el usuario exista en "users"
      const userSnap = await getDoc(doc(db, "users", dni));
      if (!userSnap.exists()) {
        setError(`No se encontró un trabajador con DNI: ${dni}`);
        return;
      }
      setDatosUsuario(userSnap.data());
      setDniActual(dni);

      // 2. Intentar cargar ficha existente desde onboarding/{dni}/fichasPersonal/actual
      const fichaRef = doc(db, `onboarding/${dni}/fichasPersonal`, "actual");
      const fichaSnap = await getDoc(fichaRef);
      if (fichaSnap.exists()) {
        const ficha = fichaSnap.data();
        setFichaExistente(ficha);
        populateFromFicha(ficha);
      }
    } catch (err) {
      console.error("Error al buscar trabajador:", err);
      setError("Error al consultar el trabajador. Intente de nuevo.");
    } finally {
      setBuscando(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Guardar ficha
  // ─────────────────────────────────────────────────────────────────────────────
  const handleGuardar = async () => {
    if (!dniActual) return;

    if (!rrhhCargo.trim()) {
      Swal.fire("Campo requerido", "El campo 'Cargo' es obligatorio.", "warning");
      return;
    }
    if (!rrhhRemuneracion.trim()) {
      Swal.fire("Campo requerido", "El campo 'Remuneración' es obligatorio.", "warning");
      return;
    }
    if (!rrhhFechaInicioLaboral) {
      Swal.fire("Campo requerido", "El campo 'Fecha inicio relación laboral' es obligatorio.", "warning");
      return;
    }

    const auth = getAuth(firebaseApp);
    const updatedBy =
      auth.currentUser?.email ||
      usuario?.email ||
      "admin@sistema.com";

    const fichaData = {
      rrhhCargo: rrhhCargo.trim().toUpperCase(),
      rrhhMoneda,
      rrhhRemuneracion: rrhhRemuneracion.trim(),
      rrhhFechaInicioLaboral,
      rrhhCompleto,
      rrhhUpdatedAt: new Date().toISOString(),
      rrhhUpdatedBy: updatedBy,
    };

    try {
      setGuardando(true);

      // Ruta principal: onboarding/{dni}/fichasPersonal/actual
      const fichaRef = doc(db, `onboarding/${dniActual}/fichasPersonal`, "actual");
      await setDoc(fichaRef, fichaData, { merge: true });

      // Espejo recomendado: fichaDatos/{dni}
      const espejoRef = doc(db, "fichaDatos", dniActual);
      await setDoc(espejoRef, fichaData, { merge: true });

      // Auditoría
      try {
        await logBusinessAction(
          "FICHA_PERSONAL_RRHH",
          "GestionOnboarding",
          {
            dniEditado: dniActual,
            cambiosRealizados: {
              rrhhCargo: {
                anterior: fichaExistente?.rrhhCargo ?? null,
                nuevo: fichaData.rrhhCargo,
              },
              rrhhRemuneracion: {
                anterior: fichaExistente?.rrhhRemuneracion ?? null,
                nuevo: fichaData.rrhhRemuneracion,
              },
              rrhhCompleto: {
                anterior: fichaExistente?.rrhhCompleto ?? null,
                nuevo: fichaData.rrhhCompleto,
              },
              rrhhFechaInicioLaboral: {
                anterior: fichaExistente?.rrhhFechaInicioLaboral ?? null,
                nuevo: fichaData.rrhhFechaInicioLaboral,
              },
            },
          },
          usuario
        );
      } catch (e) {
        console.warn("No se pudo registrar auditoría:", e);
      }

      setFichaExistente(fichaData);

      Swal.fire({
        title: "¡Guardado!",
        text: `La ficha de datos personales (RRHH) de ${datosUsuario?.name || dniActual} fue guardada correctamente.`,
        icon: "success",
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error al guardar ficha:", err);
      Swal.fire(
        "Error",
        "No se pudo guardar la ficha. Intente nuevamente.",
        "error"
      );
    } finally {
      setGuardando(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Formato de fecha legible
  // ─────────────────────────────────────────────────────────────────────────────
  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    try {
      return new Date(isoStr).toLocaleString("es-PE", {
        timeZone: "America/Lima",
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return isoStr;
    }
  };

  const descargarPlantillaMasiva = () => {
    const wb = XLSX.utils.book_new();
    const data = [
      { DNI: "41607220", ESTADO: "APERTURAR", PUESTO: "OPERADOR DE GRUA", MONEDA: "PEN", SALARIO: "3500.00", FECHA_INICIO: "2026-05-01" },
      { DNI: "45500112", ESTADO: "CERRAR", PUESTO: "", MONEDA: "", SALARIO: "", FECHA_INICIO: "" },
    ];
    const wsData = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, wsData, "Plantilla");

    const instrucciones = [
      ["CAMPO", "DETALLE"],
      ["DNI", "Obligatorio. Debe existir en colección users."],
      ["ESTADO", "Opcional. APERTURAR/ABRIR/SI/1 = abierto (rrhhCompleto=true). CERRAR/NO/0 = cerrado (rrhhCompleto=false)."],
      ["PUESTO", "Obligatorio. Se guarda en mayúscula en rrhhCargo."],
      ["MONEDA", "Obligatorio. Valor estándar: PEN, USD o EUR."],
      ["SALARIO", "Obligatorio. Monto numérico, por ejemplo 3500 o 3500.00."],
      ["FECHA_INICIO", "Obligatorio cuando envía datos laborales. Formato recomendado: YYYY-MM-DD (ejemplo: 2026-05-01)."],
      ["REGLA", "Puede enviar solo DNI+ESTADO para apertura/cierre masivo. Si llena datos laborales, debe completar PUESTO+MONEDA+SALARIO+FECHA_INICIO."],
    ];
    const wsHelp = XLSX.utils.aoa_to_sheet(instrucciones);
    XLSX.utils.book_append_sheet(wb, wsHelp, "Instrucciones");

    XLSX.writeFile(wb, "plantilla_ficha_personal_rrhh.xlsx");
  };

  const normalizarMoneda = (value) => {
    const raw = (value || "").toString().trim().toUpperCase();
    if (raw === "PEN" || raw === "S/" || raw === "SOL" || raw === "SOLES") return "S/";
    if (raw === "USD" || raw === "$" || raw === "DOLAR" || raw === "DÓLAR" || raw === "DOLARES" || raw === "DÓLARES") return "$";
    if (raw === "EUR" || raw === "€" || raw === "EURO" || raw === "EUROS") return "€";
    return null;
  };

  const normalizarEstadoCompleto = (value) => {
    const raw = (value || "").toString().trim().toUpperCase();
    if (!raw) return null;
    if (["APERTURAR", "ABRIR", "SI", "SÍ", "TRUE", "1", "COMPLETO"].includes(raw)) return true;
    if (["CERRAR", "NO", "FALSE", "0", "INCOMPLETO"].includes(raw)) return false;
    return null;
  };

  const normalizarFechaInicio = (value) => {
    if (value === null || value === undefined || value === "") return "";

    if (typeof value === "number" && Number.isFinite(value)) {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (!parsed) return null;
      const yyyy = String(parsed.y).padStart(4, "0");
      const mm = String(parsed.m).padStart(2, "0");
      const dd = String(parsed.d).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    const raw = value.toString().trim();
    if (!raw) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    const slashMatch = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (slashMatch) {
      const dd = String(Number(slashMatch[1])).padStart(2, "0");
      const mm = String(Number(slashMatch[2])).padStart(2, "0");
      const yyyy = slashMatch[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    const parsedDate = new Date(raw);
    if (!Number.isNaN(parsedDate.getTime())) {
      const yyyy = parsedDate.getFullYear();
      const mm = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const dd = String(parsedDate.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    return null;
  };

  const obtenerValor = (row, aliases) => {
    const key = Object.keys(row).find((k) => aliases.includes(k.toUpperCase().trim()));
    return key ? row[key] : "";
  };

  const leerExcel = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const first = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(first, { defval: "" });
          resolve(rows);
        } catch (e2) {
          reject(e2);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const procesarCargaMasiva = async () => {
    if (!archivoMasivo) {
      Swal.fire("Archivo requerido", "Seleccione un archivo Excel para procesar.", "warning");
      return;
    }

    try {
      setProcesandoMasivo(true);
      setResultadoMasivo({ procesados: 0, errores: 0 });
      setDetalleMasivo([]);
      const rows = await leerExcel(archivoMasivo);
      if (!rows.length) {
        Swal.fire("Sin datos", "El archivo Excel no contiene filas.", "warning");
        return;
      }

      const errores = [];
      const validos = [];
      const detalle = [];
      const updatedBy = getAuth(firebaseApp).currentUser?.email || usuario?.email || "admin@sistema.com";

      rows.forEach((row, index) => {
        const fila = index + 2;
        const dniRaw = obtenerValor(row, ["DNI"]);
        const estadoRaw = obtenerValor(row, ["ESTADO", "ACCION", "ACCIÓN", "APERTURA", "REGISTRO_COMPLETO", "REGISTRO COMPLETO"]);
        const puestoRaw = obtenerValor(row, ["PUESTO", "CARGO"]);
        const monedaRaw = obtenerValor(row, ["MONEDA"]);
        const salarioRaw = obtenerValor(row, ["SALARIO", "REMUNERACION", "REMUNERACIÓN"]);
        const fechaInicioRaw = obtenerValor(row, ["FECHA_INICIO", "FECHA INICIO", "FECHA_INICIO_LABORAL", "FECHA_INICIO_RELACION_LABORAL"]);

        const dni = (dniRaw || "").toString().trim().replace(/\.0+$/, "");
        const estadoCompleto = normalizarEstadoCompleto(estadoRaw);
        const puesto = (puestoRaw || "").toString().trim().toUpperCase();
        const moneda = normalizarMoneda(monedaRaw);
        const salario = (salarioRaw || "").toString().trim();
        const fechaInicio = normalizarFechaInicio(fechaInicioRaw);
        const fechaInicioTexto = (fechaInicioRaw || "").toString().trim();
        const salarioNum = Number(salario);
        const tieneDatosLaborales = Boolean(
          puesto || (monedaRaw || "").toString().trim() || salario || fechaInicioTexto
        );

        if (!dni) {
          detalle.push({ fila, dni: "—", estado: "ERROR", mensaje: "DNI es obligatorio." });
          errores.push({
            fila,
            dni,
            error: "DNI es obligatorio.",
          });
          return;
        }

        if (estadoCompleto === null && !tieneDatosLaborales) {
          detalle.push({
            fila,
            dni: dni || "—",
            estado: "ERROR",
            mensaje: "Debe enviar ESTADO (APERTURAR/CERRAR) o datos laborales completos.",
          });
          errores.push({
            fila,
            dni,
            error: "Debe enviar ESTADO (APERTURAR/CERRAR) o datos laborales completos (PUESTO, MONEDA, SALARIO).",
          });
          return;
        }

        if (tieneDatosLaborales && (!puesto || !moneda || !salario || !fechaInicioTexto)) {
          detalle.push({
            fila,
            dni: dni || "—",
            estado: "ERROR",
            mensaje: "Si envía datos laborales debe completar PUESTO, MONEDA, SALARIO y FECHA_INICIO.",
          });
          errores.push({
            fila,
            dni,
            error: "Si envía datos laborales debe completar PUESTO, MONEDA, SALARIO y FECHA_INICIO. MONEDA: PEN, USD o EUR.",
          });
          return;
        }

        if (tieneDatosLaborales && fechaInicio === null) {
          detalle.push({ fila, dni: dni || "—", estado: "ERROR", mensaje: "FECHA_INICIO inválida." });
          errores.push({ fila, dni, error: "FECHA_INICIO inválida. Use YYYY-MM-DD o DD/MM/YYYY." });
          return;
        }

        if (tieneDatosLaborales && (!Number.isFinite(salarioNum) || salarioNum < 0)) {
          detalle.push({ fila, dni: dni || "—", estado: "ERROR", mensaje: "SALARIO debe ser numérico y >= 0." });
          errores.push({ fila, dni, error: "SALARIO debe ser numérico y mayor o igual a 0." });
          return;
        }

        const fichaData = {
          rrhhUpdatedAt: new Date().toISOString(),
          rrhhUpdatedBy: updatedBy,
        };
        if (estadoCompleto !== null) fichaData.rrhhCompleto = estadoCompleto;
        if (tieneDatosLaborales) {
          fichaData.rrhhCargo = puesto;
          fichaData.rrhhMoneda = moneda;
          fichaData.rrhhRemuneracion = salario;
          fichaData.rrhhFechaInicioLaboral = fechaInicio;
        }

        validos.push({
          fila,
          dni,
          fichaData,
        });
      });

      let procesados = 0;
      while (validos.length) {
        const chunk = validos.splice(0, 300);
        const verificados = await Promise.all(
          chunk.map(async (item) => {
            const userSnap = await getDoc(doc(db, "users", item.dni));
            if (!userSnap.exists()) {
              detalle.push({
                fila: item.fila,
                dni: item.dni,
                estado: "ERROR",
                mensaje: "El DNI no existe en users.",
              });
              errores.push({
                fila: item.fila,
                dni: item.dni,
                error: "El DNI no existe en la colección users.",
              });
              return null;
            }
            return item;
          })
        );

        const batch = writeBatch(db);
        let chunkProcesados = 0;
        verificados.filter(Boolean).forEach((item) => {
          const fichaRef = doc(db, `onboarding/${item.dni}/fichasPersonal`, "actual");
          const espejoRef = doc(db, "fichaDatos", item.dni);
          batch.set(fichaRef, item.fichaData, { merge: true });
          batch.set(espejoRef, item.fichaData, { merge: true });
          chunkProcesados += 1;
        });

        if (chunkProcesados > 0) {
          const itemsProcesar = verificados.filter(Boolean);
          try {
            await batch.commit();
            procesados += chunkProcesados;
            itemsProcesar.forEach((item) => {
              detalle.push({
                fila: item.fila,
                dni: item.dni,
                estado: "OK",
                mensaje: "Actualizado correctamente.",
              });
            });
          } catch (batchError) {
            itemsProcesar.forEach((item) => {
              detalle.push({
                fila: item.fila,
                dni: item.dni,
                estado: "ERROR",
                mensaje: "No se pudo guardar en Firestore.",
              });
              errores.push({
                fila: item.fila,
                dni: item.dni,
                error: "No se pudo guardar en Firestore.",
              });
            });
          }
        }
      }

      if (errores.length) {
        const ws = XLSX.utils.json_to_sheet(errores);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Errores");
        XLSX.writeFile(wb, `errores_ficha_personal_${new Date().toISOString().slice(0, 10)}.xlsx`);
      }

      Swal.fire({
        title: "Carga masiva finalizada",
        html: `Procesados: <b>${procesados}</b><br/>Errores: <b>${errores.length}</b>`,
        icon: procesados > 0 ? "success" : "warning",
      });

      setResultadoMasivo({ procesados, errores: errores.length });
      setDetalleMasivo(detalle.sort((a, b) => a.fila - b.fila));
      setArchivoMasivo(null);
    } catch (err) {
      console.error("Error en carga masiva:", err);
      Swal.fire("Error", "No se pudo procesar la carga masiva.", "error");
    } finally {
      setProcesandoMasivo(false);
    }
  };

  const buildFichaPdf = () => {
    if (!datosUsuario || !dniActual) return null;

    const docPdf = new jsPDF();
    const fecha = new Date().toLocaleString("es-PE", {
      timeZone: "America/Lima",
      dateStyle: "medium",
      timeStyle: "short",
    });

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(16);
    docPdf.text("Ficha de Datos Personales - RRHH", 14, 20);

    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(11);
    docPdf.text(`Fecha de emisión: ${fecha}`, 14, 28);

    let y = 40;
    const line = (label, value) => {
      docPdf.setFont("helvetica", "bold");
      docPdf.text(`${label}:`, 14, y);
      docPdf.setFont("helvetica", "normal");
      docPdf.text(`${value || "—"}`, 60, y);
      y += 8;
    };

    line("DNI", dniActual);
    line("Trabajador", `${datosUsuario?.name || ""} ${datosUsuario?.apellidos || ""}`.trim());
    line("Email", datosUsuario?.email || "—");
    line("Puesto", rrhhCargo || "—");
    line("Moneda", rrhhMoneda || "—");
    line("Salario", rrhhRemuneracion || "—");
    line("Fecha inicio relación laboral", rrhhFechaInicioLaboral || "—");
    line("Registro completo", rrhhCompleto ? "Sí" : "No");
    line("Última actualización", formatDate(fichaExistente?.rrhhUpdatedAt));
    line("Actualizado por", fichaExistente?.rrhhUpdatedBy || "—");

    return docPdf;
  };

  const handleDescargarPdfFicha = () => {
    const docPdf = buildFichaPdf();
    if (!docPdf) {
      Swal.fire("Sin datos", "Primero busque un trabajador para generar su ficha en PDF.", "warning");
      return;
    }
    docPdf.save(`ficha_rrhh_${dniActual}.pdf`);
  };

  const handleVerPdfFicha = () => {
    const docPdf = buildFichaPdf();
    if (!docPdf) {
      Swal.fire("Sin datos", "Primero busque un trabajador para generar su ficha en PDF.", "warning");
      return;
    }
    const blobUrl = docPdf.output("bloburl");
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f0f2ff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* ── Card principal ── */}
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
        >
          {/* Header */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: GRADIENT_PRIMARY,
              borderBottom: `3px solid ${ACCENT_COLOR}`,
            }}
          >
            <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}} className="w-100">

              <div className="text-center">
                <h2 className="fw-bold mb-1">
                  <FaClipboardList className="me-3" />
                  Ficha de Datos Personales
                </h2>
                <p className="mb-0 opacity-75">
                  Complete los datos del trabajador para registrar su información laboral
                </p>
              </div>
              <div style={{position:"absolute",right:"20px"}}>
                <BotonManualHeader usuario={usuario} />
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* ── Buscador por DNI ── */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    buscarPorDNI();
                  }}
                >
                  <Row className="align-items-end g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-dark">
                          <FaIdCard className="me-2" />
                          DNI del trabajador
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            id="ficha-dni-input"
                            type="text"
                            value={dniInput}
                            onChange={(e) => setDniInput(e.target.value)}
                            placeholder="Ej. 41607220"
                            className="form-control-lg"
                            maxLength={9}
                            disabled={buscando}
                            style={{ borderRadius: "10px 0 0 10px" }}
                          />
                          <Button
                            id="ficha-buscar-btn"
                            variant="primary"
                            onClick={buscarPorDNI}
                            disabled={buscando || !dniInput.trim()}
                            className="d-flex align-items-center"
                            style={{
                              borderRadius: "0 10px 10px 0",
                              background: GRADIENT_PRIMARY,
                              border: "none",
                            }}
                          >
                            {buscando ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FaSearch />
                            )}
                            <span className="ms-2">Buscar</span>
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Button
                        id="ficha-limpiar-btn"
                        variant="outline-secondary"
                        size="lg"
                        onClick={limpiarBusqueda}
                        disabled={buscando}
                        style={{ borderRadius: "10px" }}
                        title="Limpiar"
                      >
                        <FaTimes />
                      </Button>
                    </Col>

                    <Col md={4}>
                      <Form.Text className="text-muted d-block">
                        Ingrese el DNI para cargar o crear la ficha de datos personales.
                      </Form.Text>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            {procesandoMasivo && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                style={{ zIndex: 1080 }}
              >
                <div className="bg-white p-4 rounded-3 shadow-lg" style={{ minWidth: "320px", maxWidth: "420px" }}>
                  <div className="text-center mb-3">
                    <Spinner animation="border" variant="warning" size="sm" className="me-2" />
                    <span className="fw-bold" style={{ color: "#8B4513" }}>Procesando carga masiva...</span>
                  </div>
                  <ProgressBar now={100} variant="warning" animated className="mb-2" style={{ height: "20px" }} />
                  <p className="text-center text-muted small mb-0">Validando y guardando información de RRHH</p>
                </div>
              </div>
            )}

            <Row className="mb-4 g-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{resultadoMasivo.procesados}</h4>
                    <p className="mb-0">Registros Procesados</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{resultadoMasivo.errores}</h4>
                    <p className="mb-0">Registros con Error</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {detalleMasivo.length > 0 && (
              <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                <Card.Header
                  className="text-white py-3"
                  style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)", borderRadius: "15px 15px 0 0" }}
                >
                  <h5 className="mb-0 fw-semibold">Resultado por usuario</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped hover className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>DNI</th>
                          <th>Estado</th>
                          <th>Mensaje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalleMasivo.map((item, idx) => (
                          <tr key={`${item.fila}-${item.dni}-${idx}`}>
                            <td>{item.fila}</td>
                            <td>{item.dni}</td>
                            <td>
                              <Badge bg={item.estado === "OK" ? "success" : "danger"}>
                                {item.estado}
                              </Badge>
                            </td>
                            <td>{item.mensaje}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Alert variant="info" className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <div className="d-flex align-items-center gap-2">
                <FaExclamationTriangle className="text-info" />
                <div>
                  <strong>Flujo masivo RRHH:</strong>
                  <br />
                  <small>Descarga la plantilla y usa DNI+ESTADO (APERTURAR/CERRAR) para apertura/cierre masivo; opcionalmente agrega PUESTO/MONEDA/SALARIO/FECHA_INICIO para actualizar datos laborales.</small>
                </div>
              </div>
            </Alert>

            <Row className="g-4 mb-4">
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", borderRadius: "15px 15px 0 0" }}>
                    <h5 className="mb-0 fw-semibold"><FaFileExcel className="me-2" />Plantilla Excel</h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <p className="text-muted mb-3">
                      Descarga la plantilla corporativa para aperturar/cerrar fichas por DNI y, si aplica, actualizar puesto, moneda y salario.
                    </p>
                    <Button
                      variant="success"
                      onClick={descargarPlantillaMasiva}
                      className="w-100 d-flex align-items-center justify-content-center gap-2 py-3"
                      style={{ borderRadius: "10px" }}
                    >
                      <FaDownload /> Descargar Plantilla
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", borderRadius: "15px 15px 0 0" }}>
                    <h5 className="mb-0 fw-semibold"><FaUpload className="me-2" />Subir Excel</h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form.Group controlId="ficha-masiva-file" className="mb-3">
                      <Form.Label className="fw-semibold">Archivo .xlsx / .xls</Form.Label>
                      <Form.Control
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setArchivoMasivo(e.target.files?.[0] || null)}
                        className="form-control-lg"
                        style={{ borderRadius: "10px", border: "2px dashed #dee2e6", padding: "16px" }}
                      />
                      <Form.Text className="text-muted">
                        {archivoMasivo ? `Archivo: ${archivoMasivo.name}` : "Columnas: DNI y ESTADO (APERTURAR/CERRAR). Opcionalmente: PUESTO, MONEDA, SALARIO, FECHA_INICIO."}
                      </Form.Text>
                    </Form.Group>

                    <Button
                      className="w-100 d-flex align-items-center justify-content-center gap-2 py-3 text-white"
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        border: "none",
                        borderRadius: "10px",
                      }}
                      disabled={procesandoMasivo || !archivoMasivo}
                      onClick={procesarCargaMasiva}
                    >
                      {procesandoMasivo ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Cargar masivo
                        </>
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ── Error ── */}
            {error && (
              <Alert
                variant="warning"
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Atención</h6>
                    <p className="mb-0">{error}</p>
                  </div>
                </div>
              </Alert>
            )}

            {/* ── Datos del trabajador ── */}
            {datosUsuario && (
              <Card
                className="mb-4 border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <Row className="align-items-center">
                    <Col xs="auto">
                      <div
                        className="rounded-3 p-3 text-center"
                        style={{ background: GRADIENT_PRIMARY, color: "white" }}
                      >
                        <FaUsers size={24} />
                      </div>
                    </Col>
                    <Col>
                      <h5 className="mb-1 fw-bold">
                        {datosUsuario.name} {datosUsuario.apellidos}
                      </h5>
                      <div className="d-flex flex-wrap gap-3">
                        <span className="text-muted small">
                          <strong>DNI:</strong> {dniActual}
                        </span>
                        {datosUsuario.email && (
                          <span className="text-muted small">
                            <strong>Email:</strong> {datosUsuario.email}
                          </span>
                        )}
                        {datosUsuario.cargo && (
                          <span className="text-muted small">
                            <strong>Cargo:</strong> {datosUsuario.cargo}
                          </span>
                        )}
                      </div>
                    </Col>
                    <Col xs="auto">
                      {fichaExistente ? (
                        <Badge
                          bg="success"
                          className="px-3 py-2"
                          style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                        >
                          <FaCheckCircle className="me-1" />
                          Ficha existente
                        </Badge>
                      ) : (
                        <Badge
                          bg="warning"
                          text="dark"
                          className="px-3 py-2"
                          style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                        >
                          Sin ficha previa
                        </Badge>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* ── Registro de ficha (visible solo tras buscar) ── */}
            {datosUsuario && (
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{ background: GRADIENT_PRIMARY }}
                >
                  <div className="d-flex align-items-center">
                    <FaBriefcase className="me-2" />
                    <h5 className="mb-0 fw-semibold">
                      Información Laboral
                    </h5>
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  <Row className="g-4">
                    {/* Cargo */}
                    <Col md={6}>
                      <Form.Group controlId="ficha-rrhh-cargo">
                        <Form.Label className="fw-semibold text-dark">
                          <FaBriefcase className="me-2 text-primary" />
                          Cargo
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={rrhhCargo}
                          onChange={(e) =>
                            setRrhhCargo(e.target.value.toUpperCase())
                          }
                          placeholder="Ej. OPERADOR DE GRUA"
                          style={{
                            borderRadius: "10px",
                            textTransform: "uppercase",
                            fontSize: "0.95rem",
                          }}
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>

                    {/* Moneda */}
                    <Col md={2}>
                      <Form.Group controlId="ficha-rrhh-moneda">
                        <Form.Label className="fw-semibold text-dark">
                          Moneda
                        </Form.Label>
                        <Form.Select
                          value={rrhhMoneda}
                          onChange={(e) => setRrhhMoneda(e.target.value)}
                          style={{ borderRadius: "10px", fontSize: "0.95rem" }}
                          className="py-2"
                        >
                          <option value="S/">S/ (Sol)</option>
                          <option value="$">$ (Dólar)</option>
                          <option value="€">€ (Euro)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* Remuneración */}
                    <Col md={3}>
                      <Form.Group controlId="ficha-rrhh-remuneracion">
                        <Form.Label className="fw-semibold text-dark">
                          <FaMoneyBillWave className="me-2 text-success" />
                          Remuneración
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text
                            style={{
                              borderRadius: "10px 0 0 10px",
                              background: "#e9ecef",
                              fontWeight: 700,
                              minWidth: "48px",
                              justifyContent: "center",
                            }}
                          >
                            {rrhhMoneda}
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            value={rrhhRemuneracion}
                            onChange={(e) => setRrhhRemuneracion(e.target.value)}
                            placeholder="3500.00"
                            style={{ borderRadius: "0 10px 10px 0", fontSize: "0.95rem" }}
                            className="py-2"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId="ficha-rrhh-fecha-inicio">
                        <Form.Label className="fw-semibold text-dark">
                          Fecha inicio relación laboral
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={rrhhFechaInicioLaboral}
                          onChange={(e) => setRrhhFechaInicioLaboral(e.target.value)}
                          style={{ borderRadius: "10px", fontSize: "0.95rem" }}
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group controlId="ficha-rrhh-completo">
                        <Form.Check
                          type="switch"
                          id="rrhh-completo-switch"
                          label={
                            <span className="fw-semibold">
                              Registro completo{" "}
                              <Badge
                                bg={rrhhCompleto ? "success" : "secondary"}
                                className="ms-2"
                              >
                                {rrhhCompleto ? "Sí" : "No"}
                              </Badge>
                            </span>
                          }
                          checked={rrhhCompleto}
                          onChange={(e) => setRrhhCompleto(e.target.checked)}
                          style={{ fontSize: "0.95rem" }}
                        />
                        <Form.Text className="text-muted">
                          Active esta opción cuando la información del trabajador esté completa.
                        </Form.Text>
                      </Form.Group>
                    </Col>



                    {/* Historial de la ficha existente */}
                    {fichaExistente && (
                      <Col md={12}>
                        <Alert
                          variant="info"
                          className="border-0"
                          style={{ borderRadius: "10px" }}
                        >
                          <div className="d-flex align-items-start">
                            <FaHistory className="me-2 mt-1" />
                            <div>
                              <strong>Última actualización:</strong>{" "}
                              {formatDate(fichaExistente.rrhhUpdatedAt)}
                              {fichaExistente.rrhhUpdatedBy && (
                                <>
                                  {" "}
                                  por{" "}
                                  <strong>{fichaExistente.rrhhUpdatedBy}</strong>
                                </>
                              )}
                            </div>
                          </div>
                        </Alert>
                      </Col>
                    )}

                    {/* Botón guardar */}
                    <Col md={12} className="d-flex justify-content-end gap-2 flex-wrap">
                      <Button
                        variant="outline-danger"
                        size="lg"
                        onClick={handleVerPdfFicha}
                        style={{ borderRadius: "10px", padding: "12px 20px", fontWeight: 600 }}
                      >
                        <FaEye className="me-2" />
                        Ver PDF
                      </Button>
                      <Button
                        variant="danger"
                        size="lg"
                        onClick={handleDescargarPdfFicha}
                        style={{ borderRadius: "10px", padding: "12px 20px", fontWeight: 600 }}
                      >
                        <FaFilePdf className="me-2" />
                        Descargar PDF
                      </Button>
                      <Button
                        id="ficha-guardar-btn"
                        size="lg"
                        disabled={guardando}
                        onClick={handleGuardar}
                        style={{
                          background: GRADIENT_PRIMARY,
                          border: "none",
                          borderRadius: "10px",
                          padding: "12px 32px",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
                          transition: "opacity 0.2s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.opacity = "0.88")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.opacity = "1")
                        }
                      >
                        {guardando ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando…
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" />
                            Guardar Ficha
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default FichaPersonal;


